// Fullscreen API helper functions with cross-browser support

/**
 * Request fullscreen mode (like pressing F11)
 * @param {HTMLElement} element - Element to make fullscreen (default: document.documentElement)
 * @returns {Promise} Promise that resolves when fullscreen is entered
 */
export const requestFullscreen = (element = document.documentElement) => {
  if (element.requestFullscreen) {
    return element.requestFullscreen();
  } else if (element.webkitRequestFullscreen) {
    // Safari
    return element.webkitRequestFullscreen();
  } else if (element.mozRequestFullScreen) {
    // Firefox
    return element.mozRequestFullScreen();
  } else if (element.msRequestFullscreen) {
    // IE/Edge
    return element.msRequestFullscreen();
  } else {
    return Promise.reject(new Error('Fullscreen API is not supported'));
  }
};

/**
 * Exit fullscreen mode
 * @returns {Promise} Promise that resolves when fullscreen is exited
 */
export const exitFullscreen = () => {
  if (document.exitFullscreen) {
    return document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    // Safari
    return document.webkitExitFullscreen();
  } else if (document.mozCancelFullScreen) {
    // Firefox
    return document.mozCancelFullScreen();
  } else if (document.msExitFullscreen) {
    // IE/Edge
    return document.msExitFullscreen();
  } else {
    return Promise.reject(new Error('Fullscreen API is not supported'));
  }
};

/**
 * Check if currently in fullscreen mode
 * @returns {boolean} True if in fullscreen mode
 */
export const isFullscreen = () => {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
};

/**
 * Get the fullscreen change event name for the current browser
 * @returns {string} Event name
 */
export const getFullscreenChangeEvent = () => {
  if (document.fullscreenElement !== undefined) {
    return 'fullscreenchange';
  } else if (document.webkitFullscreenElement !== undefined) {
    return 'webkitfullscreenchange';
  } else if (document.mozFullScreenElement !== undefined) {
    return 'mozfullscreenchange';
  } else if (document.msFullscreenElement !== undefined) {
    return 'MSFullscreenChange';
  }
  return 'fullscreenchange';
};

